// What the code declares, and C# name lookup over it: which type a name means in a given place,
// what a type's bases and members are, and which extension methods exist. Types from packages and
// the framework are not declared here; they become `ext` types known only by name.
import type { CsClass, CsFile, CsMethod, CsParameter, CsProperty, Expr, Span, TypeRef } from '../csharp/ast';
import type { LineMap } from '../csharp/diagnostics';
import { PREDEFINED_TYPES, typeText } from '../csharp/parser';
import type { CodeMemberKind, CodeTypeKind } from './model';

/** A parsed C# file, ready for the code map. */
export interface ParsedCodeFile {
  path: string;
  text: string;
  file: CsFile;
  lines: LineMap;
  lineCount: number;
  /** Statements or members the parser could not read. */
  problems: number;
}

/** The type of an expression, as far as it can be known. */
export type Ty =
  | { k: 'code'; sym: TypeSymbol; args: Ty[] }
  | { k: 'ext'; name: string; args: Ty[] }
  | { k: 'param'; name: string }
  | { k: 'array'; elem: Ty }
  | { k: 'tuple'; items: Ty[] }
  /** A type written in an expression, for static access: `OrderService.Create()`. */
  | { k: 'type'; ty: Ty }
  /** A namespace written in an expression: `Shop.Orders`. */
  | { k: 'ns'; name: string }
  | { k: 'unknown' };

export const UNKNOWN: Ty = { k: 'unknown' };

export const ext = (name: string, args: Ty[] = []): Ty => ({ k: 'ext', name, args });

/**
 * A value from the framework or a package whose type the table does not know. Unlike UNKNOWN, a
 * call on it is a framework call, never a guess at a member of the code.
 */
export const OPAQUE: Ty = ext('?');

/** Type parameter name to the type it stands for. */
export type Subst = Map<string, Ty>;

export interface FileContext {
  path: string;
  project: string | null;
  /** The file's usings and its project's global usings. */
  usings: string[];
  staticUsings: string[];
  aliases: Map<string, TypeRef>;
}

/** Where a name is looked up: the file, the type and method around it. */
export interface Where {
  ctx: FileContext;
  type: TypeSymbol | null;
  /** Type parameters of the method (and local functions) around the code. */
  typeParams: readonly string[];
  /** The namespace around the code when there is no type around it (a top-level type's base list). */
  namespace?: string | null;
}

export type MemberDecl =
  | { kind: 'method'; method: CsMethod }
  | { kind: 'property'; property: CsProperty }
  | { kind: 'field'; type: TypeRef; init: Expr | null }
  | { kind: 'enumMember'; value: Expr | null }
  /** A positional record parameter, which declares a property. */
  | { kind: 'recordParam'; param: CsParameter };

export interface MemberSymbol {
  id: string;
  name: string;
  kind: CodeMemberKind;
  owner: TypeSymbol;
  /** The part of a partial type that declares it, and that part's file. */
  cls: CsClass;
  ctx: FileContext;
  decl: MemberDecl;
  params: CsParameter[];
  typeParams: string[];
  /** Return type, or the type of a property, field or event; null for constructors. */
  valueType: TypeRef | null;
  isStatic: boolean;
  /** A static method whose first parameter has `this`. */
  isExtension: boolean;
  modifiers: string[];
  signature: string;
  line: number;
  hasBody: boolean;
}

export interface Bases {
  /** The base class, when it is declared in the code. */
  base: Ty | null;
  /** Interfaces declared in the code. */
  interfaces: Ty[];
  /** Base types from the framework or packages (`List<Order>`, `DbContext`, `IEnumerable<T>`). */
  externals: Ty[];
  /** The base class is from the framework or a package. */
  externalBase: boolean;
}

export class TypeSymbol {
  readonly id: string;
  readonly members: MemberSymbol[] = [];
  readonly byName = new Map<string, MemberSymbol[]>();
  readonly nested = new Map<string, TypeSymbol[]>();
  readonly parts: { cls: CsClass; ctx: FileContext }[] = [];
  /** Resolved lazily: the base class and the interfaces, with this type's parameters unresolved. */
  bases: Bases | null = null;

  constructor(
    readonly name: string,
    readonly kind: CodeTypeKind,
    readonly namespace: string | null,
    readonly outer: TypeSymbol | null,
    readonly typeParams: string[],
  ) {
    const tick = typeParams.length ? `\`${typeParams.length}` : '';
    this.id = outer ? `${outer.id}+${name}${tick}` : namespace ? `${namespace}.${name}${tick}` : `${name}${tick}`;
  }

  get arity(): number {
    return this.typeParams.length;
  }

  /** `Repository<TEntity, TKey>` */
  get display(): string {
    return this.arity ? `${this.name}<${this.typeParams.join(', ')}>` : this.name;
  }

  addMember(member: MemberSymbol): void {
    this.members.push(member);
    const list = this.byName.get(member.name);
    if (list) list.push(member);
    else this.byName.set(member.name, [member]);
  }
}

/** C# keywords and their framework names, so `String` and `string` are the same type. */
const KEYWORD_OF: Record<string, string> = {
  String: 'string', Int32: 'int', Int64: 'long', Int16: 'short', Byte: 'byte', SByte: 'sbyte', UInt32: 'uint', UInt64: 'ulong',
  UInt16: 'ushort', Boolean: 'bool', Char: 'char', Decimal: 'decimal', Double: 'double', Single: 'float', Object: 'object',
  IntPtr: 'nint', UIntPtr: 'nuint', Void: 'void',
};

/** The name a framework or package type is known by: the last part, keywords for the built-in types. */
export function externalName(name: string): string {
  const last = name.slice(name.lastIndexOf('.') + 1);
  return KEYWORD_OF[last] ?? last;
}

function parentNamespace(ns: string): string | null {
  const i = ns.lastIndexOf('.');
  return i < 0 ? null : ns.slice(0, i);
}

const TYPE_KINDS: Record<CsClass['keyword'], CodeTypeKind> = {
  class: 'class',
  struct: 'struct',
  record: 'record',
  interface: 'interface',
  enum: 'enum',
  delegate: 'delegate',
};

/** `Order`, `List<Order>`, `int[]`: a parameter type for signatures and ids. */
function paramTypes(params: CsParameter[]): string {
  return params.map((p) => (p.type ? typeText(p.type) : '?')).join(', ');
}

export class SymbolTable {
  readonly types = new Map<string, TypeSymbol>();
  /** Namespace to simple name to the types with that name (several arities). */
  private readonly namespaces = new Map<string, Map<string, TypeSymbol[]>>();
  /** Every namespace declared, with its parents (to tell `Shop.Orders.X` apart from `order.X`). */
  readonly namespaceNames = new Set<string>();
  readonly members = new Map<string, MemberSymbol>();
  /** Extension methods by name. */
  readonly extensions = new Map<string, MemberSymbol[]>();
  /** Methods by name, for guesses. */
  readonly methodsByName = new Map<string, MemberSymbol[]>();
  readonly contexts = new Map<string, FileContext>();
  /** Types in declaration order, with the file of each part. */
  readonly typeOrder: TypeSymbol[] = [];

  /** `projectUsings`: the `<Using Include="..." />` items of a project, global usings of its files. */
  constructor(files: ParsedCodeFile[], projectOf: (path: string) => string | null, projectUsings: (project: string | null) => string[] = () => []) {
    // Global usings and aliases apply to every file of their project.
    const globalUsings = new Map<string | null, Set<string>>();
    const globalAliases = new Map<string | null, Map<string, TypeRef>>();
    for (const f of files) {
      const project = projectOf(f.path);
      const set = globalUsings.get(project) ?? new Set<string>(projectUsings(project));
      for (const u of f.file.globalUsings) set.add(u);
      globalUsings.set(project, set);
      const aliases = globalAliases.get(project) ?? new Map<string, TypeRef>();
      for (const a of f.file.aliases) if (a.global) aliases.set(a.name, a.target);
      globalAliases.set(project, aliases);
    }
    for (const f of files) {
      const project = projectOf(f.path);
      const aliases = new Map(globalAliases.get(project) ?? []);
      for (const a of f.file.aliases) aliases.set(a.name, a.target);
      this.contexts.set(f.path, {
        path: f.path,
        project,
        usings: [...new Set([...f.file.usings, ...(globalUsings.get(project) ?? [])])],
        staticUsings: f.file.staticUsings,
        aliases,
      });
    }
    // Types first (outer types come before the types nested in them), then members.
    const declared: { sym: TypeSymbol; cls: CsClass; file: ParsedCodeFile }[] = [];
    for (const f of files) {
      const ctx = this.contexts.get(f.path)!;
      const byFullName = new Map<string, TypeSymbol>();
      for (const cls of f.file.classes) {
        const outer = cls.outer ? (byFullName.get(cls.outer) ?? null) : null;
        const sym = this.declareType(cls, outer);
        sym.parts.push({ cls, ctx });
        byFullName.set(cls.fullName, sym);
        declared.push({ sym, cls, file: f });
      }
    }
    for (const { sym, cls, file } of declared) this.declareMembers(sym, cls, file);
  }

  private declareType(cls: CsClass, outer: TypeSymbol | null): TypeSymbol {
    const probe = new TypeSymbol(cls.name, TYPE_KINDS[cls.keyword], cls.namespace, outer, cls.typeParams);
    const existing = this.types.get(probe.id);
    if (existing) return existing; // another part of a partial type
    this.types.set(probe.id, probe);
    this.typeOrder.push(probe);
    if (outer) {
      const list = outer.nested.get(probe.name) ?? [];
      list.push(probe);
      outer.nested.set(probe.name, list);
    } else {
      const ns = cls.namespace ?? '';
      const byName = this.namespaces.get(ns) ?? new Map<string, TypeSymbol[]>();
      byName.set(probe.name, [...(byName.get(probe.name) ?? []), probe]);
      this.namespaces.set(ns, byName);
      for (let n: string | null = cls.namespace; n; n = parentNamespace(n)) this.namespaceNames.add(n);
    }
    return probe;
  }

  private declareMembers(sym: TypeSymbol, cls: CsClass, file: ParsedCodeFile): void {
    const ctx = this.contexts.get(file.path)!;
    const lineOf = (span: Span) => file.lines.at(span.start).line;
    const isStaticType = cls.modifiers.includes('static');
    const add = (m: Omit<MemberSymbol, 'id' | 'owner' | 'cls' | 'ctx'> & { idPart: string }) => {
      let id = `${sym.id}.${m.idPart}`;
      // The same member declared twice (both branches of an #if): keep both, apart.
      for (let n = 2; this.members.has(id); n++) id = `${sym.id}.${m.idPart}#${n}`;
      const { idPart: _, ...rest } = m;
      const member: MemberSymbol = { ...rest, id, owner: sym, cls, ctx };
      this.members.set(id, member);
      sym.addMember(member);
      if (member.isExtension) this.extensions.set(member.name, [...(this.extensions.get(member.name) ?? []), member]);
      if (member.kind === 'method') this.methodsByName.set(member.name, [...(this.methodsByName.get(member.name) ?? []), member]);
    };

    // Positional records declare a property for each parameter.
    if (cls.keyword === 'record' && cls.primaryParams) {
      for (const p of cls.primaryParams) {
        if (!p.type) continue;
        add({
          idPart: p.name,
          name: p.name,
          kind: 'property',
          decl: { kind: 'recordParam', param: p },
          params: [],
          typeParams: [],
          valueType: p.type,
          isStatic: false,
          isExtension: false,
          modifiers: ['public'],
          signature: p.name,
          line: lineOf(p.type.span),
          hasBody: false,
        });
      }
    }
    for (const m of cls.methods) {
      const isStatic = m.modifiers.includes('static') || isStaticType;
      const params = `(${paramTypes(m.params)})`;
      const generic = m.typeParams.length ? `<${m.typeParams.join(', ')}>` : '';
      const idPart =
        m.kind === 'constructor' ? `${isStatic ? '#cctor' : '#ctor'}${params}` : m.kind === 'destructor' ? `${m.name}()` : `${m.explicitInterface ? `${typeText(m.explicitInterface)}.` : ''}${m.name}${generic}${params}`;
      const display = m.kind === 'constructor' ? `${cls.name}${params}` : `${m.name}${generic}${params}`;
      add({
        idPart,
        name: m.kind === 'constructor' ? '.ctor' : m.name,
        kind: m.kind,
        decl: { kind: 'method', method: m },
        params: m.params,
        typeParams: m.typeParams,
        valueType: m.returnType,
        isStatic,
        isExtension: isStatic && m.kind === 'method' && !!m.params[0]?.modifiers?.includes('this'),
        modifiers: m.modifiers,
        signature: display,
        line: lineOf(m.nameSpan),
        hasBody: m.body !== null,
      });
    }
    for (const p of cls.properties) {
      const indexer = p.kind === 'indexer';
      const idPart = `${p.explicitInterface ? `${typeText(p.explicitInterface)}.` : ''}${indexer ? `this[${paramTypes(p.params)}]` : p.name}`;
      add({
        idPart,
        name: indexer ? 'this[]' : p.name,
        kind: p.kind,
        decl: { kind: 'property', property: p },
        params: p.params,
        typeParams: [],
        valueType: p.type,
        isStatic: p.modifiers.includes('static') || isStaticType,
        isExtension: false,
        modifiers: p.modifiers,
        signature: indexer ? `this[${paramTypes(p.params)}]` : p.name,
        line: lineOf(p.nameSpan),
        hasBody: p.expressionBody !== null || p.accessors.some((a) => a.body !== null),
      });
    }
    for (const f of cls.fields) {
      for (const d of f.declarators) {
        add({
          idPart: d.name,
          name: d.name,
          kind: f.kind,
          decl: { kind: 'field', type: f.type, init: d.init },
          params: [],
          typeParams: [],
          valueType: f.type,
          isStatic: f.modifiers.includes('static') || f.kind === 'const' || isStaticType,
          isExtension: false,
          modifiers: f.modifiers,
          signature: d.name,
          line: lineOf(d.nameSpan),
          hasBody: true,
        });
      }
    }
    for (const e of cls.enumMembers) {
      add({
        idPart: e.name,
        name: e.name,
        kind: 'enumMember',
        decl: { kind: 'enumMember', value: e.value },
        params: [],
        typeParams: [],
        valueType: null,
        isStatic: true,
        isExtension: false,
        modifiers: [],
        signature: e.name,
        line: lineOf(e.nameSpan),
        hasBody: true,
      });
    }
  }

  // ---------------------------------------------------------------- type names

  private typeIn(ns: string, name: string, arity: number): TypeSymbol | null {
    return this.namespaces.get(ns)?.get(name)?.find((t) => t.arity === arity) ?? null;
  }

  /** A type nested in `sym` or in one of its base types. */
  private nestedIn(sym: TypeSymbol, name: string, arity: number, seen = new Set<TypeSymbol>()): TypeSymbol | null {
    if (seen.has(sym)) return null;
    seen.add(sym);
    const own = sym.nested.get(name)?.find((t) => t.arity === arity);
    if (own) return own;
    const base = this.basesOf(sym).base;
    return base?.k === 'code' ? this.nestedIn(base.sym, name, arity, seen) : null;
  }

  /** The type a simple name means at `where`, as C# looks it up; a type parameter; or null (not in the code). */
  lookupName(name: string, arity: number, where: Where): TypeSymbol | { param: string } | null {
    if (arity === 0 && where.typeParams.includes(name)) return { param: name };
    for (let t = where.type; t; t = t.outer) {
      if (arity === 0 && t.typeParams.includes(name)) return { param: name };
      const nested = this.nestedIn(t, name, arity);
      if (nested) return nested;
      if (t.name === name && t.arity === arity) return t;
    }
    const outermost = (() => {
      let t = where.type;
      while (t?.outer) t = t.outer;
      return t;
    })();
    for (let ns: string | null = outermost ? outermost.namespace : (where.namespace ?? null); ; ns = parentNamespace(ns)) {
      const found = this.typeIn(ns ?? '', name, arity);
      if (found) return found;
      if (!ns) break;
    }
    const fromUsings = new Set<TypeSymbol>();
    for (const u of where.ctx.usings) {
      const found = this.typeIn(u, name, arity);
      if (found) fromUsings.add(found);
    }
    if (fromUsings.size === 1) return [...fromUsings][0]!;
    if (arity === 0) {
      const alias = where.ctx.aliases.get(name);
      if (alias) {
        const target = this.resolve(alias, { ctx: { ...where.ctx, aliases: new Map() }, type: null, typeParams: [] });
        if (target.k === 'code') return target.sym;
      }
    }
    for (const s of where.ctx.staticUsings) {
      const host = this.resolveQualified(s, 0, where);
      const nested = host ? host.nested.get(name)?.find((t) => t.arity === arity) : undefined;
      if (nested) return nested;
    }
    return null;
  }

  /** A dotted name: an alias, a namespace and type, or a type and the types nested in it. */
  private resolveQualified(name: string, arity: number, where: Where): TypeSymbol | null {
    const parts = name.split('.');
    if (parts.length === 1) {
      const found = this.lookupName(name, arity, where);
      return found instanceof TypeSymbol ? found : null;
    }
    const walk = (start: TypeSymbol | null, rest: string[]): TypeSymbol | null => {
      let t = start;
      rest.forEach((part, i) => {
        t = t?.nested.get(part)?.find((n) => n.arity === (i === rest.length - 1 ? arity : 0)) ?? null;
      });
      return t;
    };
    // Namespace prefixes, absolute first, then relative to the namespaces around the code.
    const enclosing: string[] = [''];
    let outermost = where.type;
    while (outermost?.outer) outermost = outermost.outer;
    for (let ns = outermost ? outermost.namespace : (where.namespace ?? null); ns; ns = parentNamespace(ns)) enclosing.unshift(`${ns}.`);
    for (const prefix of enclosing.reverse()) {
      for (let i = parts.length - 1; i >= 1; i--) {
        const ns = prefix + parts.slice(0, i).join('.');
        if (!this.namespaces.has(ns)) continue;
        const head = this.typeIn(ns, parts[i]!, i === parts.length - 1 ? arity : 0);
        const found = head ? walk(head, parts.slice(i + 1)) : null;
        if (found) return found;
      }
    }
    const first = this.lookupName(parts[0]!, 0, where);
    if (first instanceof TypeSymbol) return walk(first, parts.slice(1));
    return null;
  }

  /** The type a written type reference means at `where`. */
  resolve(ref: TypeRef, where: Where): Ty {
    let ty: Ty;
    if (ref.tuple) ty = { k: 'tuple', items: ref.tuple.map((t) => this.resolve(t, where)) };
    else if (PREDEFINED_TYPES.has(ref.name)) ty = ext(ref.name);
    else {
      const args = ref.args.map((a) => this.resolve(a, where));
      const found = ref.name.includes('.') ? this.resolveQualified(ref.name, args.length, where) : this.lookupName(ref.name, args.length, where);
      const alias = !found && !args.length && !ref.name.includes('.') ? where.ctx.aliases.get(ref.name) : undefined;
      if (found instanceof TypeSymbol) ty = { k: 'code', sym: found, args };
      else if (found) ty = { k: 'param', name: found.param };
      // `using OrderList = List<Order>;` (an alias cannot refer to another alias)
      else if (alias) ty = this.resolve(alias, { ...where, ctx: { ...where.ctx, aliases: new Map() } });
      else {
        const name = externalName(ref.name);
        // `Nullable<T>` is T for everything the code map does.
        ty = name === 'Nullable' && args.length === 1 ? args[0]! : ext(name, args);
      }
    }
    for (let i = 0; i < ref.arrays.length; i++) ty = { k: 'array', elem: ty };
    return ty;
  }

  // ---------------------------------------------------------------- bases and members

  /** The base class and interfaces of a type, with its own type parameters left as parameters. */
  basesOf(sym: TypeSymbol): Bases {
    if (sym.bases) return sym.bases;
    // Set first, so a type that (wrongly) inherits from itself does not loop.
    sym.bases = { base: null, interfaces: [], externals: [], externalBase: false };
    let base: Ty | null = null;
    const interfaces: Ty[] = [];
    const externals: Ty[] = [];
    let externalBase = false;
    for (const part of sym.parts) {
      const where: Where = { ctx: part.ctx, type: sym.outer ?? null, typeParams: sym.typeParams, namespace: sym.namespace };
      part.cls.baseTypes.forEach((ref, i) => {
        const ty = this.resolve(ref, where);
        if (ty.k === 'code') {
          if (ty.sym.kind === 'interface') interfaces.push(ty);
          else if (!base && sym.kind !== 'interface') base = ty;
        } else if (ty.k === 'ext') {
          externals.push(ty);
          if (i === 0 && sym.kind !== 'interface' && sym.kind !== 'struct' && !looksLikeInterface(ty.name)) externalBase = true;
        }
      });
    }
    sym.bases = { base, interfaces, externals, externalBase };
    return sym.bases;
  }

  /** Framework and package types `ty` derives from or implements, in terms of its type arguments. */
  externalBases(ty: Ty): Ty[] {
    return this.hierarchy(ty).flatMap(({ sym, subst }) => this.basesOf(sym).externals.map((e) => substitute(e, subst)));
  }

  /** The type itself, its base classes, then every interface, each with the type arguments it gets. */
  hierarchy(ty: Ty): { sym: TypeSymbol; subst: Subst }[] {
    if (ty.k !== 'code') return [];
    const out: { sym: TypeSymbol; subst: Subst }[] = [];
    const seen = new Set<TypeSymbol>();
    const interfaces: Ty[] = [];
    // Each base comes with its type arguments already in terms of the receiver's.
    let current: Ty | null = ty;
    while (current?.k === 'code' && !seen.has(current.sym)) {
      const { sym, args }: { sym: TypeSymbol; args: Ty[] } = current;
      seen.add(sym);
      const own: Subst = new Map(sym.typeParams.map((p, i) => [p, args[i] ?? { k: 'param', name: p }]));
      out.push({ sym, subst: own });
      const bases = this.basesOf(sym);
      for (const i of bases.interfaces) interfaces.push(substitute(i, own));
      current = bases.base ? substitute(bases.base, own) : null;
    }
    while (interfaces.length) {
      const i = interfaces.shift()!;
      if (i.k !== 'code' || seen.has(i.sym)) continue;
      seen.add(i.sym);
      const own: Subst = new Map(i.sym.typeParams.map((p, n) => [p, i.args[n] ?? { k: 'param', name: p }]));
      out.push({ sym: i.sym, subst: own });
      for (const next of this.basesOf(i.sym).interfaces) interfaces.push(substitute(next, own));
    }
    return out;
  }

  /** Whether the type, or a class it derives from, inherits from a framework or package class. */
  hasExternalBase(ty: Ty): boolean {
    return this.hierarchy(ty).some(({ sym }) => this.basesOf(sym).externalBase);
  }

  /** Whether `sub` is `sup`, derives from it or implements it. */
  isAssignable(sub: TypeSymbol, sup: TypeSymbol): boolean {
    return this.hierarchy({ k: 'code', sym: sub, args: [] }).some((h) => h.sym === sup);
  }
}

/** `IRepository`, `IEnumerable`: an interface by the usual naming. */
export function looksLikeInterface(name: string): boolean {
  return name.length > 1 && name[0] === 'I' && name[1]! >= 'A' && name[1]! <= 'Z';
}

export function substitute(ty: Ty, subst: Subst): Ty {
  if (subst.size === 0) return ty;
  switch (ty.k) {
    case 'param':
      return subst.get(ty.name) ?? ty;
    case 'code':
      return ty.args.length ? { k: 'code', sym: ty.sym, args: ty.args.map((a) => substitute(a, subst)) } : ty;
    case 'ext':
      return ty.args.length ? { k: 'ext', name: ty.name, args: ty.args.map((a) => substitute(a, subst)) } : ty;
    case 'array':
      return { k: 'array', elem: substitute(ty.elem, subst) };
    case 'tuple':
      return { k: 'tuple', items: ty.items.map((t) => substitute(t, subst)) };
    case 'type':
      return { k: 'type', ty: substitute(ty.ty, subst) };
    default:
      return ty;
  }
}

/** Short name for messages and tests: `List<Order>`, `Shop.Order`. */
export function tyText(ty: Ty): string {
  switch (ty.k) {
    case 'code':
      return ty.args.length ? `${ty.sym.name}<${ty.args.map(tyText).join(', ')}>` : ty.sym.name;
    case 'ext':
      return ty.args.length ? `${ty.name}<${ty.args.map(tyText).join(', ')}>` : ty.name;
    case 'param':
      return ty.name;
    case 'array':
      return `${tyText(ty.elem)}[]`;
    case 'tuple':
      return `(${ty.items.map(tyText).join(', ')})`;
    case 'type':
      return `type ${tyText(ty.ty)}`;
    case 'ns':
      return `namespace ${ty.name}`;
    default:
      return '?';
  }
}
